import React from "react";
import {
  Shield,
  Award,
  Users,
  Heart,
  Clock,
  CheckCircle,
  Phone,
} from "lucide-react";
import { TeamMembers, Values, Stats } from "../constants";

const AboutUs = () => {
  return (
    <div className="relative bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 pt-28 pb-20 sm:py-24 lg:py-32 overflow-hidden">
      {/* Animated glow effect */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-500/10 via-transparent to-orange-600/5" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-orange-500/20 rounded-full blur-[120px] glow-pulse" />
      </div>

      {/* Subtle grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />

      {/* Dark overlay for better readability */}
      <div className="absolute inset-0 bg-slate-900/60" />

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-orange-500/10 border border-orange-500/20 px-4 py-2 mb-6">
            <Users className="h-4 w-4 text-orange-400" />
            <span className="text-sm font-medium text-orange-300">
              Trusted Local Experts
            </span>
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
            About{" "}
            <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">
              Our Team
            </span>
          </h1>
          <p className="mt-6 text-lg text-slate-300 sm:text-xl max-w-2xl mx-auto leading-relaxed">
            Providing safe, reliable gas line services to our community since
            2009. Your trusted partner for all gas installation and repair
            needs.
          </p>
        </div>

        {/* Stats Section */}
        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {Stats.map((stat, index) => (
            <div
              key={stat.label}
              style={{ animationDelay: `${index * 0.1}s` }}
              className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 text-center hover:border-orange-500/50 transition-all fade-in-up"
            >
              <div className="text-3xl sm:text-4xl font-bold text-orange-400 mb-2">
                {stat.number}
              </div>
              <div className="text-slate-300 text-sm sm:text-base">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
        {/* Team Section */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 rounded-full bg-orange-500/10 border border-orange-500/20 px-3 py-1 mb-4">
              <Users className="h-4 w-4 text-orange-400" />
              <span className="text-xs font-medium text-orange-300">
                MEET THE TEAM
              </span>
            </div>
            <h2 className="text-3xl font-bold text-white sm:text-4xl mb-4">
              Your Licensed Professionals
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              Experienced technicians dedicated to your safety and satisfaction
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {TeamMembers.map((member, index) => (
              <div
                key={member.name}
                style={{ animationDelay: `${index * 0.2}s` }}
                className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden hover:border-orange-500/50 transition-all duration-300 fade-in-up group"
              >
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {member.name}
                  </h3>
                  <p className="text-orange-400 font-medium mb-4">
                    {member.role}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1 text-xs bg-slate-700/50 text-slate-300 px-3 py-1 rounded-full">
                      <CheckCircle className="h-3 w-3 text-green-400" />
                      Licensed
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs bg-slate-700/50 text-slate-300 px-3 py-1 rounded-full">
                      <CheckCircle className="h-3 w-3 text-green-400" />
                      Insured
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs bg-slate-700/50 text-slate-300 px-3 py-1 rounded-full">
                      <CheckCircle className="h-3 w-3 text-green-400" />
                      Certified
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Story Section */}
        <div className="mt-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 rounded-full bg-orange-500/10 border border-orange-500/20 px-3 py-1 mb-6">
                <Heart className="h-4 w-4 text-orange-400" />
                <span className="text-xs font-medium text-orange-300">
                  OUR STORY
                </span>
              </div>
              <h2 className="text-3xl font-bold text-white mb-6">
                Built on Trust, Powered by Expertise
              </h2>
              <div className="space-y-4 text-slate-300 leading-relaxed">
                <p>
                  Founded in 2009, our company has been serving the local
                  community with professional gas line services for over 15
                  years. What started as a commitment to providing safe,
                  reliable gas installations has grown into a trusted name in
                  the industry.
                </p>
                <p>
                  We believe that every home and business deserves the highest
                  standard of gas service. That's why we've built our reputation
                  on three pillars: safety, quality, and customer satisfaction.
                  Our team of licensed professionals brings decades of combined
                  experience to every project, no matter the size.
                </p>
                <p>
                  From simple repairs to complex installations, we approach each
                  job with the same level of dedication and attention to detail.
                  We're not just service providers—we're your neighbors,
                  committed to keeping our community safe and comfortable.
                </p>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=800&h=600&fit=crop"
                  alt="Gas service work"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
              </div>
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 rounded-full bg-orange-500/10 border border-orange-500/20 px-3 py-1 mb-4">
              <Shield className="h-4 w-4 text-orange-400" />
              <span className="text-xs font-medium text-orange-300">
                CORE VALUES
              </span>
            </div>
            <h2 className="text-3xl font-bold text-white sm:text-4xl mb-4">
              What Sets Us Apart
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              Our values guide every decision we make and every service we
              provide
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={value.title}
                  style={{ animationDelay: `${index * 0.1}s` }}
                  className="bg-slate-800/90 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 hover:border-orange-500/50 transition-all duration-300 fade-in-up"
                >
                  <div className="rounded-lg p-3 bg-orange-500/10 inline-flex mb-4">
                    <Icon className="h-8 w-8 text-orange-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">
                    {value.title}
                  </h3>
                  <p className="text-slate-300 text-sm">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Certifications Section */}
        <div className="mt-20 bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 sm:p-12">
          <div className="text-center mb-8">
            <Award className="h-12 w-12 text-orange-400 mx-auto mb-4" />
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Licensed, Insured & Certified
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              We maintain all necessary licenses, insurance, and certifications
              to provide you with the highest quality gas line services. Your
              safety and peace of mind are our top priorities.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            <div className="flex items-center gap-3 bg-slate-800/50 rounded-lg p-4">
              <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
              <span className="text-slate-300 text-sm">
                State Licensed Contractors
              </span>
            </div>
            <div className="flex items-center gap-3 bg-slate-800/50 rounded-lg p-4">
              <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
              <span className="text-slate-300 text-sm">
                Fully Insured Services
              </span>
            </div>
            <div className="flex items-center gap-3 bg-slate-800/50 rounded-lg p-4">
              <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
              <span className="text-slate-300 text-sm">
                OSHA Safety Certified
              </span>
            </div>
            <div className="flex items-center gap-3 bg-slate-800/50 rounded-lg p-4">
              <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
              <span className="text-slate-300 text-sm">Background Checked</span>
            </div>
            <div className="flex items-center gap-3 bg-slate-800/50 rounded-lg p-4">
              <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
              <span className="text-slate-300 text-sm">
                Continuing Education
              </span>
            </div>
            <div className="flex items-center gap-3 bg-slate-800/50 rounded-lg p-4">
              <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
              <span className="text-slate-300 text-sm">Code Compliance</span>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Work With Us?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Experience the difference that professional, reliable gas line
            service makes. Contact us today for a free consultation.
          </p>
          <div className="inline-flex flex-col sm:flex-row gap-4">
            <a
              href="tel:9805406064"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 px-8 py-4 text-lg font-semibold text-white shadow-lg hover:shadow-orange-500/50 hover:scale-105 transition-all"
            >
              <Phone className="h-5 w-5" />
              Call (980) 540-6064
            </a>
            {/* <a
              href="request-appointment"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-800 border-2 border-slate-600 px-8 py-4 text-lg font-semibold text-white hover:bg-slate-700 transition-all"
            >
              temp comment 
               Get Free Quote
            </a> */}
          </div>
          <p className="mt-6 text-sm text-slate-400">
            Serving the community since 2009 • Licensed & Insured • Free
            estimates
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes glowPulse {
          0%, 100% {
            opacity: 0.3;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        .fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }
        
        .glow-pulse {
          animation: glowPulse 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default AboutUs;
